import { atom } from 'jotai';
import request from 'lib/request';
import { Response } from 'lib/request/types';
import { FailedApiResponse } from 'components/well-page-content/types';
import {
  AreaConfigResponse,
  TypeCurveScenarioFormData,
  GlobalConfig,
  AreaConfig,
  FormationMapping,
  IntersectShapesResponse,
  ValidateAreaConfigResponse,
  InventoryConfig,
  InventoryConfigResponse,
  ScalingConfigRow,
  ScalingConfigResponse,
  TypeWellConfigRow,
  TypeWellConfigResponse,
  GeneralValidationRule,
  StreamThreshold,
  ValidationConfigs,
  ValidationConfigResponse,
} from './types';
import { formationPriorityLabelToValue } from './default-values';
import {
  DcaScenariosResponse,
  WellDcaScenariosResponse,
  Resource,
} from 'types/api-responses';
import searchAtomPair, { SearchParams } from 'atoms/search-atom';

// --- Area Geometry Search ---

export interface AreaGeometryResult {
  id: number;
  href: string;
  name: string;
  displayName: string;
  category: string;
  grossAcres: number;
  shared: boolean;
  active: boolean;
  createdBy: string;
  dataSource: string;
  purpose: string;
  structureGridCollectionId: number | null;
  createdAt: string;
  updatedAt: string;
  resources: {
    comments: Resource;
    events: Resource;
    structureGridCollections?: { href: string; auth: string[] };
  };
}

export interface AreaGeometryFormSearchParams extends SearchParams {
  text?: string;
  category?: string;
  active?: boolean;
  shared?: boolean;
}

export const createAreaGeometryCategoriesAtom = () => {
  return searchAtomPair<AreaGeometryResult, AreaGeometryFormSearchParams>(
    'areaGeometries',
    { page: 1, pageSize: 1 }
  );
};

export const createAreaGeometryDetailsSearchAtom = () => {
  return searchAtomPair<AreaGeometryResult, AreaGeometryFormSearchParams>(
    'areaGeometries'
  );
};

interface TypeCurveScenarioDataAtomType {
  loading: boolean;
  error: string | null;
}

export const typeCurveScenarioDataAtom = atom<TypeCurveScenarioDataAtomType>({
  loading: false,
  error: null,
});

// --- Save (create) area config ---

interface SaveAreaConfigPayload {
  payload: {
    typeCurveScenarioId: string;
    areaConfig: AreaConfig;
  };
  onSuccess?: (data: { areaConfig: AreaConfigResponse }) => void;
}

const buildSubAreaPayload = (
  subAreas: AreaConfig['subAreas'],
  subAreaCategory: AreaConfig['subAreaCategory']
) => {
  return subAreas.map((s) => {
    // s.id is always the actual area id (type curve area or development area).
    // s.associationId is the join table id (only present for saved sub-areas).
    const entry: Record<string, any> = {};

    if (s.associationId) {
      entry.id = Number(s.associationId);
    }

    if (subAreaCategory === 'type_curve_area') {
      entry.typeCurveAreaId = Number(s.id);
    } else {
      entry.developmentAreaId = Number(s.id);
    }

    return entry;
  });
};

const buildAreaConfigPayload = (areaConfig: AreaConfig) => {
  const payload: Record<string, any> = {
    areaGeometryId: areaConfig.areaGeometryId
      ? Number(areaConfig.areaGeometryId)
      : null,
    basinName: areaConfig.basinName,
    algorithmicFormationPriority: areaConfig.algorithmicFormationPriority,
  };

  // developmentType: omit when 'All'
  if (areaConfig.developmentType && areaConfig.developmentType !== 'All') {
    payload.developmentType = areaConfig.developmentType.toLowerCase();
  }

  // Optional fields — only include when present
  if (areaConfig.structureGridCollectionId) {
    payload.structureGridCollectionId = Number(
      areaConfig.structureGridCollectionId
    );
  }

  if (areaConfig.subAreaCategory) {
    payload.subAreaCategory = areaConfig.subAreaCategory;
  }

  if (areaConfig.operatorNames && areaConfig.operatorNames.length > 0) {
    payload.operatorNames = areaConfig.operatorNames;
  }

  if (areaConfig.subAreas && areaConfig.subAreas.length > 0) {
    payload.subAreas = buildSubAreaPayload(
      areaConfig.subAreas,
      areaConfig.subAreaCategory
    );
  }

  return payload;
};

export const saveAreaConfigAtom = atom(
  (get) => get(typeCurveScenarioDataAtom),
  async (get, set, data: SaveAreaConfigPayload) => {
    set(typeCurveScenarioDataAtom, { loading: true, error: null });

    try {
      const { payload } = data;
      const response = await request.post<
        AreaConfigResponse,
        FailedApiResponse
      >(
        `/type_curve_scenarios/${payload.typeCurveScenarioId}/area_configs`,
        { areaConfig: buildAreaConfigPayload(payload.areaConfig) },
        new AbortController()
      );

      if (response.type === 'error') {
        const errors = response.data?.errors;
        let msg = '';
        errors?.forEach((error) => {
          msg += `${error.message}\n`;
        });
        throw new Error(msg || 'Error creating area config');
      } else if (response.type !== 'success') {
        throw new Error('Error creating area config');
      }

      set(typeCurveScenarioDataAtom, { loading: false, error: null });
      data?.onSuccess?.({ areaConfig: response.data });
    } catch (error) {
      set(typeCurveScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error ? error.message : 'Error creating area config',
      });
      throw error;
    }
  }
);

// --- Update area config ---

interface UpdateAreaConfigPayload {
  payload: {
    typeCurveScenarioId: string;
    areaConfigId: string;
    areaConfig: Partial<AreaConfig>;
    fullAreaConfig: AreaConfig;
  };
  onSuccess?: (data: { areaConfig: AreaConfigResponse }) => void;
}

const buildUpdateAreaConfigPayload = (
  changedFields: Partial<AreaConfig>,
  fullAreaConfig: AreaConfig
) => {
  const payload: Record<string, any> = {};

  if ('areaGeometryId' in changedFields) {
    payload.areaGeometryId = changedFields.areaGeometryId
      ? Number(changedFields.areaGeometryId)
      : null;
  }

  if ('basinName' in changedFields) {
    payload.basinName = changedFields.basinName;
  }

  if ('developmentType' in changedFields) {
    if (
      changedFields.developmentType &&
      changedFields.developmentType !== 'All'
    ) {
      payload.developmentType = changedFields.developmentType.toLowerCase();
    }
  }

  if (
    'structureGridCollectionId' in changedFields &&
    changedFields.structureGridCollectionId
  ) {
    payload.structureGridCollectionId = Number(
      changedFields.structureGridCollectionId
    );
  }

  if ('subAreaCategory' in changedFields && changedFields.subAreaCategory) {
    payload.subAreaCategory = changedFields.subAreaCategory;
  }

  if ('algorithmicFormationPriority' in changedFields) {
    payload.algorithmicFormationPriority =
      changedFields.algorithmicFormationPriority;
  }

  if ('operatorNames' in changedFields) {
    payload.operatorNames = changedFields.operatorNames || [];
  }

  if ('subAreas' in changedFields) {
    payload.subAreas = changedFields.subAreas?.length
      ? buildSubAreaPayload(
          changedFields.subAreas,
          fullAreaConfig.subAreaCategory
        )
      : [];
  }

  return payload;
};

export const updateAreaConfigAtom = atom(
  (get) => get(typeCurveScenarioDataAtom),
  async (get, set, data: UpdateAreaConfigPayload) => {
    set(typeCurveScenarioDataAtom, { loading: true, error: null });

    try {
      const { payload } = data;
      const response = await request.patch<
        AreaConfigResponse,
        FailedApiResponse
      >(
        `/type_curve_scenarios/${payload.typeCurveScenarioId}/area_configs/${payload.areaConfigId}`,
        {
          areaConfig: buildUpdateAreaConfigPayload(
            payload.areaConfig,
            payload.fullAreaConfig
          ),
        },
        new AbortController()
      );

      if (response.type === 'error') {
        const errors = response.data?.errors;
        let msg = '';
        errors?.forEach((error) => {
          msg += `${error.message}\n`;
        });
        throw new Error(msg || 'Error updating area config');
      } else if (response.type !== 'success') {
        throw new Error('Error updating area config');
      }

      set(typeCurveScenarioDataAtom, { loading: false, error: null });
      data?.onSuccess?.({ areaConfig: response.data });
    } catch (error) {
      set(typeCurveScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error ? error.message : 'Error updating area config',
      });
      throw error;
    }
  }
);

// --- Delete area config ---

const deleteAreaConfigAtomBase = atom<{
  loading: boolean;
  error: string | null;
}>({ loading: false, error: null });

export const deleteAreaConfigAtom = atom(
  (get) => get(deleteAreaConfigAtomBase),
  async (
    get,
    set,
    payload: {
      url: string;
      onSuccess?: () => void;
    }
  ) => {
    set(deleteAreaConfigAtomBase, { loading: true, error: null });
    try {
      // The backend deletes the area config's formation mappings, inventory,
      // type well, scaling and validation configs along with it.
      await request.deleteReq<null, null>(
        payload.url,
        null,
        new AbortController()
      );

      set(deleteAreaConfigAtomBase, { loading: false, error: null });
      payload?.onSuccess?.();
    } catch (error) {
      set(deleteAreaConfigAtomBase, {
        loading: false,
        error:
          error instanceof Error ? error.message : 'Error deleting area config',
      });
    }
  }
);

// --- Update scenario (global config) ---

interface UpdateScenarioPayload {
  payload: {
    typeCurveScenarioId: string;
    globalConfig: Partial<GlobalConfig>;
  };
  onSuccess?: () => void;
}

export const updateScenarioAtom = atom(
  (get) => get(typeCurveScenarioDataAtom),
  async (get, set, data: UpdateScenarioPayload) => {
    set(typeCurveScenarioDataAtom, { loading: true, error: null });

    try {
      const { payload } = data;
      const response = await request.patch<any, FailedApiResponse>(
        `/type_curve_scenarios/${payload.typeCurveScenarioId}`,
        {
          typeCurveScenario: {
            name: payload.globalConfig.name,
            status: payload.globalConfig.status,
            dcaScenarioId: payload.globalConfig.dcaScenarioId,
            notes: payload.globalConfig.notes,
          },
        },
        new AbortController()
      );

      if (response.type === 'error') {
        const errors = response.data?.errors;
        let msg = '';
        errors?.forEach((error) => {
          msg += `${error.message}\n`;
        });
        throw new Error(msg || 'Error updating scenario');
      } else if (response.type !== 'success') {
        throw new Error('Error updating scenario');
      }

      set(typeCurveScenarioDataAtom, { loading: false, error: null });
      data?.onSuccess?.();
    } catch (error) {
      set(typeCurveScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error ? error.message : 'Error updating scenario',
      });
      throw error;
    }
  }
);

// --- Intersect shapes ---

interface IntersectShapesPayload {
  payload: {
    source_geometries: Array<{ model_type: string; model_id: number }>;
    target_models: string[];
    coverage_threshold: number;
  };
  onSuccess?: (data: IntersectShapesResponse) => void;
}

const intersectShapesBaseAtom = atom<{
  loading: boolean;
  error: string | null;
}>({ loading: false, error: null });

export const intersectShapesAtom = atom(
  (get) => get(intersectShapesBaseAtom),
  async (get, set, data: IntersectShapesPayload) => {
    set(intersectShapesBaseAtom, { loading: true, error: null });

    try {
      const response = await request.post<
        IntersectShapesResponse,
        FailedApiResponse
      >('/queries/intersect_shapes', data.payload, new AbortController());

      if (response.type === 'error') {
        throw new Error('Error fetching intersect shapes');
      } else if (response.type !== 'success') {
        throw new Error('Error fetching intersect shapes');
      }

      set(intersectShapesBaseAtom, { loading: false, error: null });
      data?.onSuccess?.(response.data);
    } catch (error) {
      set(intersectShapesBaseAtom, {
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Error fetching intersect shapes',
      });
    }
  }
);

// --- Fetch operators via wells query ---

interface FetchOperatorsPayload {
  payload: {
    query: {
      page: number;
      pageSize: number;
      useElastic: boolean;
      holeDirectionIds: string[];
      polygonGeojson?: object;
    };
  };
  onSuccess?: (operators: string[]) => void;
}

const fetchOperatorsBaseAtom = atom<{
  loading: boolean;
  error: string | null;
}>({ loading: false, error: null });

export const fetchOperatorsAtom = atom(
  (get) => get(fetchOperatorsBaseAtom),
  async (get, set, data: FetchOperatorsPayload) => {
    set(fetchOperatorsBaseAtom, { loading: true, error: null });

    try {
      const response = await request.post<any, FailedApiResponse>(
        '/queries/wells',
        data.payload,
        new AbortController()
      );

      if (response.type !== 'success') {
        throw new Error('Error fetching operators');
      }

      const facets = response.data?.facets;
      const operators = (facets?.currentOperator?.buckets ?? [])
        .map((b: { id: string; name: string }) => b.name)
        .filter(Boolean)
        .sort();

      set(fetchOperatorsBaseAtom, { loading: false, error: null });
      data?.onSuccess?.(operators);
    } catch (error) {
      set(fetchOperatorsBaseAtom, {
        loading: false,
        error:
          error instanceof Error ? error.message : 'Error fetching operators',
      });
    }
  }
);

// --- Fetch sub-area detail (development area or type curve area) ---

interface FetchSubAreaDetailPayload {
  href: string;
  onSuccess?: (data: any) => void;
}

const fetchSubAreaDetailBaseAtom = atom<{
  loading: boolean;
  error: string | null;
}>({ loading: false, error: null });

export const fetchSubAreaDetailAtom = atom(
  (get) => get(fetchSubAreaDetailBaseAtom),
  async (get, set, data: FetchSubAreaDetailPayload) => {
    set(fetchSubAreaDetailBaseAtom, { loading: true, error: null });

    try {
      const response = await request.get<any>(data.href, new AbortController());

      if ('error' in response) {
        throw new Error('Error fetching sub area detail');
      }

      set(fetchSubAreaDetailBaseAtom, { loading: false, error: null });
      data?.onSuccess?.(response);
    } catch (error) {
      set(fetchSubAreaDetailBaseAtom, {
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Error fetching sub area detail',
      });
    }
  }
);

// --- Save full scenario (combined global + area config for new) ---

interface SaveTypeCurveScenarioPayload {
  payload: {
    globalConfig: GlobalConfig;
    areaConfig?: AreaConfig | null;
    formData: Partial<TypeCurveScenarioFormData>;
  };
  onSuccess?: (data: {
    typeCurveScenarioId: string;
    areaConfig?: AreaConfigResponse;
  }) => void;
}

export const saveTypeCurveScenarioAtom = atom(
  (get) => get(typeCurveScenarioDataAtom),
  async (get, set, data: SaveTypeCurveScenarioPayload) => {
    set(typeCurveScenarioDataAtom, { loading: true, error: null });

    try {
      const { payload } = data;

      // Create scenario
      const scenarioResponse = await request.post<any, FailedApiResponse>(
        '/type_curve_scenarios',
        {
          typeCurveScenario: {
            name: payload.globalConfig.name,
            status: payload.globalConfig.status,
            dcaScenarioId: payload.globalConfig.dcaScenarioId,
            notes: payload.globalConfig.notes,
          },
        },
        new AbortController()
      );

      if (scenarioResponse.type === 'error') {
        const errors = scenarioResponse.data?.errors;
        let msg = '';
        errors?.forEach((error) => {
          msg += `${error.message}\n`;
        });
        const { details } = errors?.[0] || {};
        if (details && typeof details === 'object') {
          const detailMsg = Object.entries(details)
            .map(([key, value]) => `${key}: ${value}`)
            .join('\n');
          if (detailMsg) msg = detailMsg;
        }
        throw new Error(msg || 'Error creating scenario');
      } else if (scenarioResponse.type !== 'success') {
        throw new Error('Error creating scenario');
      }

      const scenarioId = scenarioResponse.data.id;

      // Create area config if provided
      let areaConfigResult: AreaConfigResponse | undefined;
      if (payload.areaConfig) {
        const areaConfigResponse = await request.post<
          AreaConfigResponse,
          FailedApiResponse
        >(
          `/type_curve_scenarios/${scenarioId}/area_configs`,
          { areaConfig: buildAreaConfigPayload(payload.areaConfig) },
          new AbortController()
        );

        if (areaConfigResponse.type === 'error') {
          const errors = areaConfigResponse.data?.errors;
          let msg = '';
          errors?.forEach((error) => {
            msg += `${error.message}\n`;
          });
          throw new Error(msg || 'Error creating area config');
        } else if (areaConfigResponse.type !== 'success') {
          throw new Error('Error creating area config');
        }

        areaConfigResult = areaConfigResponse.data;
      }

      set(typeCurveScenarioDataAtom, { loading: false, error: null });
      data?.onSuccess?.({
        typeCurveScenarioId: scenarioId,
        areaConfig: areaConfigResult,
      });
    } catch (error) {
      set(typeCurveScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error ? error.message : 'Error creating scenario',
      });
      throw error;
    }
  }
);

// --- DCA Scenarios (local to this form) ---

interface DcaScenariosState {
  loading: boolean;
  data: DcaScenariosResponse[];
  error: string | null;
}

export const dcaScenariosListAtom = atom<DcaScenariosState>({
  loading: false,
  data: [],
  error: null,
});

export const fetchDcaScenariosAtom = atom(
  (get) => get(dcaScenariosListAtom),
  async (get, set) => {
    const current = get(dcaScenariosListAtom);
    if (current.data.length > 0 || current.loading) return;

    set(dcaScenariosListAtom, { loading: true, data: [], error: null });

    try {
      const response = await request.post<
        WellDcaScenariosResponse,
        FailedApiResponse
      >(
        '/queries/reservoir/dca_scenarios',
        {
          query: {
            page: 1,
            pageSize: 50,
            sort_attribute: 'created_at',
            sort_direction: 'DESC',
          },
        },
        new AbortController()
      );

      if (response.type !== 'success') {
        throw new Error('Error fetching DCA scenarios');
      }

      set(dcaScenariosListAtom, {
        loading: false,
        data: response.data.results,
        error: null,
      });
    } catch (error) {
      set(dcaScenariosListAtom, {
        loading: false,
        data: [],
        error:
          error instanceof Error
            ? error.message
            : 'Error fetching DCA scenarios',
      });
    }
  }
);

// --- Save formation mappings ---

interface SaveFormationMappingsPayload {
  payload: {
    typeCurveScenarioId: string;
    areaConfigId: string;
    formationMappings: FormationMapping[];
    baselineFormationMappings: FormationMapping[];
    // When true, the area config uses BE-derived ordering from
    // positional_index and `formation_rank` should be omitted from the
    // payload (it's a UI-only value in algorithmic mode). When false, the
    // user has manually re-ordered rows and `formation_rank` is meaningful.
    algorithmicFormationPriority: boolean;
  };
  onSuccess?: (updatedMappings: FormationMapping[]) => void;
}

const buildFormationMappingPayload = (
  fm: FormationMapping,
  algorithmicFormationPriority: boolean
) => {
  const payload: Record<string, any> = {
    isSentinel: fm.isSentinel,
    displayColor: fm.color,
    positionalIndex: fm.positionalIndex,
  };

  // Only persist `formationRank` when the user is in manual-priority mode.
  // In algorithmic mode the BE orders rows by positional_index, so the
  // rank we computed client-side is incidental and shouldn't be saved.
  if (!algorithmicFormationPriority) {
    payload.formationRank = fm.formationRank;
  }

  if (fm.geologicIntervalId !== null) {
    payload.geologicIntervalId = fm.geologicIntervalId;
  }

  if (fm.structureGridId !== null) {
    payload.structureGridId = fm.structureGridId;
  }

  if (fm.typeCurveFormation !== null) {
    payload.typeCurveFormation = fm.typeCurveFormation;
  }

  return payload;
};

const isFormationDirty = (
  current: FormationMapping,
  baseline: FormationMapping,
  algorithmicFormationPriority: boolean
) => {
  const rankDirty =
    !algorithmicFormationPriority &&
    current.formationRank !== baseline.formationRank;
  return (
    current.color !== baseline.color ||
    current.typeCurveFormation !== baseline.typeCurveFormation ||
    rankDirty ||
    current.structureGridId !== baseline.structureGridId ||
    current.geologicIntervalId !== baseline.geologicIntervalId
  );
};

const buildChangedFields = (
  current: FormationMapping,
  baseline: FormationMapping,
  algorithmicFormationPriority: boolean
) => {
  const payload: Record<string, any> = {};

  if (current.color !== baseline.color) {
    payload.displayColor = current.color;
  }
  if (current.typeCurveFormation !== baseline.typeCurveFormation) {
    payload.typeCurveFormation = current.typeCurveFormation;
  }
  if (
    !algorithmicFormationPriority &&
    current.formationRank !== baseline.formationRank
  ) {
    payload.formationRank = current.formationRank;
  }
  if (current.structureGridId !== baseline.structureGridId) {
    payload.structureGridId = current.structureGridId;
  }
  if (current.geologicIntervalId !== baseline.geologicIntervalId) {
    payload.geologicIntervalId = current.geologicIntervalId;
  }

  return payload;
};

export const saveFormationMappingsAtom = atom(
  (get) => get(typeCurveScenarioDataAtom),
  async (get, set, data: SaveFormationMappingsPayload) => {
    const { payload } = data;
    const {
      typeCurveScenarioId,
      areaConfigId,
      formationMappings,
      baselineFormationMappings,
      algorithmicFormationPriority,
    } = payload;

    const baseUrl = `/type_curve_scenarios/${typeCurveScenarioId}/area_configs/${areaConfigId}/formation_mappings`;

    // Index baselines by id
    const baselineById = new Map<string, FormationMapping>();
    baselineFormationMappings.forEach((fm) => {
      if (fm.id) baselineById.set(fm.id, fm);
    });

    // Find dirty formations by comparing with baseline
    const newFormations: FormationMapping[] = [];
    const dirtyFormations: { id: string; payload: Record<string, any> }[] = [];

    for (const fm of formationMappings) {
      if (fm.id === null) {
        // No id — always new, will POST
        newFormations.push(fm);
      } else {
        const baseline = baselineById.get(fm.id);
        if (
          !baseline ||
          isFormationDirty(fm, baseline, algorithmicFormationPriority)
        ) {
          // Has id and is dirty — will PATCH
          dirtyFormations.push({
            id: fm.id,
            payload: baseline
              ? buildChangedFields(fm, baseline, algorithmicFormationPriority)
              : buildFormationMappingPayload(fm, algorithmicFormationPriority),
          });
        }
      }
    }

    try {
      const patchPromises = dirtyFormations.map(
        ({ id, payload: updatePayload }) =>
          request.patch<any, FailedApiResponse>(
            `${baseUrl}/${id}`,
            { formationMapping: updatePayload },
            new AbortController()
          )
      );

      const postPromises = newFormations.map((fm) =>
        request.post<any, FailedApiResponse>(
          baseUrl,
          {
            formationMapping: buildFormationMappingPayload(
              fm,
              algorithmicFormationPriority
            ),
          },
          new AbortController()
        )
      );

      const patchResults = await Promise.all(patchPromises);
      const postResults = await Promise.all(postPromises);

      for (const response of [...patchResults, ...postResults]) {
        if (response.type === 'error') {
          const errors = response.data?.errors;
          let msg = '';
          errors?.forEach((error: any) => {
            msg += `${error.message}\n`;
          });
          throw new Error(msg || 'Error saving formation mapping');
        } else if (response.type !== 'success') {
          throw new Error('Error saving formation mapping');
        }
      }

      // Build updated mappings with server-assigned IDs for new formations
      let postIndex = 0;
      const updatedMappings = formationMappings.map((fm) => {
        if (fm.id !== null) return fm;
        const response = postResults[postIndex++];
        if (response?.type === 'success' && response.data?.id !== null) {
          return { ...fm, id: String(response.data.id) };
        }
        return fm;
      });

      data?.onSuccess?.(updatedMappings);
    } catch (error) {
      set(typeCurveScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Error saving formation mappings',
      });
      throw error;
    }
  }
);

// --- Save inventory config ---

const buildInventoryConfigPayload = (config: InventoryConfig) => ({
  formationPriorityOrder: config.formationPriority.map(
    formationPriorityLabelToValue
  ),
  eurDefinitionScope: config.eurBasis,
  eurDefinitionStream: config.eurStream,
  depthPriority: config.depthOrder,
  allowOverflow: config.allowOverflow,
  allowPartialSlots: config.allowPartialSlots,
  minimumSlotLengthFt: config.minSlotLength,
  includeActiveUpsideLtds: config.infillAssumptions.includeActiveUpsideLtds,
  formationLimit: config.infillAssumptions.formationLimitPercent,
  developmentAreaLimit: config.infillAssumptions.developmentAreaLimitPercent,
  ltdMinimum: config.infillAssumptions.ltdMinimum,
});

interface SaveInventoryConfigPayload {
  payload: {
    typeCurveScenarioId: string;
    areaConfigId: string;
    inventoryConfig: InventoryConfig;
  };
  onSuccess?: (data: InventoryConfigResponse) => void;
}

export const saveInventoryConfigAtom = atom(
  (get) => get(typeCurveScenarioDataAtom),
  async (get, set, data: SaveInventoryConfigPayload) => {
    try {
      const { payload } = data;
      const response = await request.post<
        InventoryConfigResponse,
        FailedApiResponse
      >(
        `/type_curve_scenarios/${payload.typeCurveScenarioId}/area_configs/${payload.areaConfigId}/inventory_configs`,
        {
          inventoryConfig: buildInventoryConfigPayload(payload.inventoryConfig),
        },
        new AbortController()
      );

      if (response.type === 'error') {
        const errors = response.data?.errors;
        let msg = '';
        errors?.forEach((error) => {
          msg += `${error.message}\n`;
        });
        throw new Error(msg || 'Error creating inventory config');
      } else if (response.type !== 'success') {
        throw new Error('Error creating inventory config');
      }

      data?.onSuccess?.(response.data);
    } catch (error) {
      set(typeCurveScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Error creating inventory config',
      });
      throw error;
    }
  }
);

// --- Update inventory config ---

interface UpdateInventoryConfigPayload {
  payload: {
    inventoryConfigHref: string;
    inventoryConfig: InventoryConfig;
  };
  onSuccess?: (data: InventoryConfigResponse) => void;
}

export const updateInventoryConfigAtom = atom(
  (get) => get(typeCurveScenarioDataAtom),
  async (get, set, data: UpdateInventoryConfigPayload) => {
    try {
      const { payload } = data;
      const response = await request.patch<
        InventoryConfigResponse,
        FailedApiResponse
      >(
        payload.inventoryConfigHref,
        {
          inventoryConfig: buildInventoryConfigPayload(payload.inventoryConfig),
        },
        new AbortController()
      );

      if (response.type === 'error') {
        const errors = response.data?.errors;
        let msg = '';
        errors?.forEach((error) => {
          msg += `${error.message}\n`;
        });
        throw new Error(msg || 'Error updating inventory config');
      } else if (response.type !== 'success') {
        throw new Error('Error updating inventory config');
      }

      data?.onSuccess?.(response.data);
    } catch (error) {
      set(typeCurveScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Error updating inventory config',
      });
      throw error;
    }
  }
);

// --- Save scaling configs ---

interface SaveScalingConfigsPayload {
  payload: {
    typeCurveScenarioId: string;
    areaConfigId: string;
    scalingConfigs: ScalingConfigRow[];
    baselineScalingConfigs: ScalingConfigRow[];
  };
  onSuccess?: (updatedConfigs: ScalingConfigRow[]) => void;
}

const buildScalingConfigPayload = (sc: ScalingConfigRow) => ({
  scalingVariable: sc.scalingVariable,
  scalingMethod: sc.scalingMethod,
  degradationFactor: sc.degradationFactorPercent,
  degradationBaseFt: sc.degradationBase,
});

const isScalingDirty = (
  current: ScalingConfigRow,
  baseline: ScalingConfigRow
) =>
  current.scalingVariable !== baseline.scalingVariable ||
  current.scalingMethod !== baseline.scalingMethod ||
  current.degradationFactorPercent !== baseline.degradationFactorPercent ||
  current.degradationBase !== baseline.degradationBase;

const buildScalingChangedFields = (
  current: ScalingConfigRow,
  baseline: ScalingConfigRow
) => {
  const payload: Record<string, any> = {};
  if (current.scalingVariable !== baseline.scalingVariable)
    payload.scalingVariable = current.scalingVariable;
  if (current.scalingMethod !== baseline.scalingMethod)
    payload.scalingMethod = current.scalingMethod;
  if (current.degradationFactorPercent !== baseline.degradationFactorPercent)
    payload.degradationFactor = current.degradationFactorPercent;
  if (current.degradationBase !== baseline.degradationBase)
    payload.degradationBaseFt = current.degradationBase;
  return payload;
};

export const saveScalingConfigsAtom = atom(
  (get) => get(typeCurveScenarioDataAtom),
  async (get, set, data: SaveScalingConfigsPayload) => {
    const { payload } = data;
    const {
      typeCurveScenarioId,
      areaConfigId,
      scalingConfigs,
      baselineScalingConfigs,
    } = payload;

    const baseUrl = `/type_curve_scenarios/${typeCurveScenarioId}/area_configs/${areaConfigId}/scaling_configs`;

    // Index baselines by id
    const baselineById = new Map<string, ScalingConfigRow>();
    baselineScalingConfigs.forEach((sc) => {
      if (sc.id) baselineById.set(sc.id, sc);
    });

    const newConfigs: ScalingConfigRow[] = [];
    const dirtyConfigs: { id: string; payload: Record<string, any> }[] = [];
    const currentIds = new Set<string>();

    for (const sc of scalingConfigs) {
      if (sc.id === null) {
        newConfigs.push(sc);
      } else {
        currentIds.add(sc.id);
        const baseline = baselineById.get(sc.id);
        if (baseline && isScalingDirty(sc, baseline)) {
          dirtyConfigs.push({
            id: sc.id,
            payload: buildScalingChangedFields(sc, baseline),
          });
        }
      }
    }

    // Find deleted configs (in baseline but not in current)
    const deletedIds = baselineScalingConfigs
      .filter((sc) => sc.id && !currentIds.has(sc.id))
      .map((sc) => sc.id!);

    try {
      const patchPromises = dirtyConfigs.map(({ id, payload: updatePayload }) =>
        request.patch<ScalingConfigResponse, FailedApiResponse>(
          `${baseUrl}/${id}`,
          { scalingConfig: updatePayload },
          new AbortController()
        )
      );

      const postPromises = newConfigs.map((sc) =>
        request.post<ScalingConfigResponse, FailedApiResponse>(
          baseUrl,
          { scalingConfig: buildScalingConfigPayload(sc) },
          new AbortController()
        )
      );

      const deletePromises = deletedIds.map((id) =>
        request.deleteReq<null, null>(
          `${baseUrl}/${id}`,
          null,
          new AbortController()
        )
      );

      const patchResults = await Promise.all(patchPromises);
      const postResults = await Promise.all(postPromises);
      await Promise.all(deletePromises);

      for (const response of [...patchResults, ...postResults]) {
        if (response.type === 'error') {
          const errors = response.data?.errors;
          let msg = '';
          errors?.forEach((error: any) => {
            msg += `${error.message}\n`;
          });
          throw new Error(msg || 'Error saving scaling config');
        } else if (response.type !== 'success') {
          throw new Error('Error saving scaling config');
        }
      }

      // Build updated configs with server-assigned IDs for new configs
      let postIndex = 0;
      const updatedConfigs = scalingConfigs.map((sc) => {
        if (sc.id !== null) return sc;
        const response = postResults[postIndex++];
        if (response?.type === 'success' && response.data?.id) {
          return { ...sc, id: String(response.data.id) };
        }
        return sc;
      });

      data?.onSuccess?.(updatedConfigs);
    } catch (error) {
      set(typeCurveScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Error saving scaling configs',
      });
      throw error;
    }
  }
);

// --- Save type well configs ---

interface SaveTypeWellConfigsPayload {
  payload: {
    typeCurveScenarioId: string;
    areaConfigId: string;
    typeWellConfigs: TypeWellConfigRow[];
    baselineTypeWellConfigs: TypeWellConfigRow[];
  };
  /**
   * Called with the rows to show, with the ids of rows the save created, and
   * the rows as the server now has them. It is also called when part of the
   * save failed, before the error is thrown, so a retry sends only what is
   * still unsaved.
   */
  onSaved?: (rows: TypeWellConfigRow[], savedRows: TypeWellConfigRow[]) => void;
}

/**
 * Joins the error messages of a failed request into one message.
 * @param {Response<unknown, FailedApiResponse>} response - The failed response
 * @return {string} The message
 */
const typeWellErrorMessage = (
  response: Response<unknown, FailedApiResponse>
): string => {
  if (response.type === 'error') {
    const messages = (response.data?.errors ?? []).map(
      (error) => error.message
    );
    if (messages.length > 0) return messages.join('\n');
  }
  return 'Error saving type well config';
};

/**
 * Checks whether a delete failed only because the row was already gone, which
 * happens when a delete from an earlier, partly failed save is sent again.
 * @param {Response<unknown, FailedApiResponse>} response - The response
 * @return {boolean} True when the row no longer exists
 */
const isAlreadyDeleted = (
  response: Response<unknown, FailedApiResponse>
): boolean =>
  response.type === 'error' &&
  (response.data?.errors ?? []).some(
    (error) => error.code === 'TYPE_WELL_CONFIG_NOT_FOUND'
  );

/**
 * Builds the request body for a Type Well Profile Config row.
 * @param {TypeWellConfigRow} config - The row
 * @return {object} The request body
 */
const buildTypeWellConfigPayload = (config: TypeWellConfigRow) => ({
  isFallback: config.isFallback,
  typeCurveFormation: config.isFallback ? null : config.tcFormationName,
  mainStream: config.mainStream,
  aggregation: config.typeWellAggregation,
  dcaScenarioId: config.dcaScenarioId ? Number(config.dcaScenarioId) : null,
  gasShrinkPercentage: config.gasShrinkPct,
  nglYield: config.nglYield,
  waterForecastOption: config.waterForecastOption,
  waterForecastSource: config.waterForecastSource,
  watercutPct: config.watercutPct,
  waterGasRatioInBblPerMmcf: config.wgr,
});

/**
 * Builds the request body for updating a saved Type Well Profile Config row,
 * with only the fields that changed.
 * @param {TypeWellConfigRow} current - The row as it is now
 * @param {TypeWellConfigRow} baseline - The row as it was saved
 * @return {Record<string, any>} The changed fields
 */
const buildTypeWellChangedFields = (
  current: TypeWellConfigRow,
  baseline: TypeWellConfigRow
) => {
  const full = buildTypeWellConfigPayload(current);
  const saved = buildTypeWellConfigPayload(baseline);
  const payload: Record<string, any> = {};
  for (const key of Object.keys(full) as (keyof typeof full)[]) {
    if (full[key] !== saved[key]) payload[key] = full[key];
  }
  return payload;
};

export const saveTypeWellConfigsAtom = atom(
  (get) => get(typeCurveScenarioDataAtom),
  async (get, set, data: SaveTypeWellConfigsPayload) => {
    const { payload } = data;
    const {
      typeCurveScenarioId,
      areaConfigId,
      typeWellConfigs,
      baselineTypeWellConfigs,
    } = payload;

    const baseUrl = `/type_curve_scenarios/${typeCurveScenarioId}/area_configs/${areaConfigId}/type_well_configs`;

    // Index baselines by id
    const baselineById = new Map<string, TypeWellConfigRow>();
    baselineTypeWellConfigs.forEach((tw) => {
      if (tw.id) baselineById.set(tw.id, tw);
    });

    const newConfigs: TypeWellConfigRow[] = [];
    const dirtyConfigs: { id: string; payload: Record<string, any> }[] = [];
    const currentIds = new Set<string>();

    for (const tw of typeWellConfigs) {
      if (tw.id === null) {
        newConfigs.push(tw);
      } else {
        currentIds.add(tw.id);
        const baseline = baselineById.get(tw.id);
        const changedFields = baseline
          ? buildTypeWellChangedFields(tw, baseline)
          : {};
        if (Object.keys(changedFields).length > 0) {
          dirtyConfigs.push({ id: tw.id, payload: changedFields });
        }
      }
    }

    // Find deleted configs (in baseline but not in current)
    const deletedIds = baselineTypeWellConfigs
      .filter((tw) => tw.id && !currentIds.has(tw.id))
      .map((tw) => tw.id!);

    try {
      // Delete first. When a formation is cleared and then added back, its
      // old row is deleted and a new row is created with the same name. The
      // backend only accepts the new row once the old one is gone.
      const deleteResults = await Promise.all(
        deletedIds.map((id) =>
          request.deleteReq<null, FailedApiResponse>(
            `${baseUrl}/${id}`,
            null,
            new AbortController()
          )
        )
      );
      const deletedSet = new Set(
        deletedIds.filter(
          (_, i) =>
            deleteResults[i].type === 'success' ||
            isAlreadyDeleted(deleteResults[i])
        )
      );
      const failedDelete = deleteResults.find(
        (_, i) => !deletedSet.has(deletedIds[i])
      );

      // A failed delete stops the save here. Creating a row could then fail
      // only because the old row with the same name is still there, which
      // would hide the real error.
      const patchResults = failedDelete
        ? []
        : await Promise.all(
            dirtyConfigs.map(({ id, payload: updatePayload }) =>
              request.patch<TypeWellConfigResponse, FailedApiResponse>(
                `${baseUrl}/${id}`,
                { typeWellConfig: updatePayload },
                new AbortController()
              )
            )
          );
      const postResults = failedDelete
        ? []
        : await Promise.all(
            newConfigs.map((tw) =>
              request.post<TypeWellConfigResponse, FailedApiResponse>(
                baseUrl,
                { typeWellConfig: buildTypeWellConfigPayload(tw) },
                new AbortController()
              )
            )
          );

      // The rows to show, with server-assigned ids for the rows that were
      // created. postResults is in the same order as the rows with no id.
      let postIndex = 0;
      const rows = typeWellConfigs.map((tw) => {
        if (tw.id !== null) return tw;
        const response = postResults[postIndex++];
        if (response?.type === 'success' && response.data?.id) {
          const resp = response.data;
          return {
            ...tw,
            id: String(resp.id),
            dcaScenarioId: resp.dcaScenario
              ? String(resp.dcaScenario.id)
              : tw.dcaScenarioId,
            dcaScenarioName: resp.dcaScenario?.name ?? tw.dcaScenarioName,
          };
        }
        return tw;
      });

      // The rows as the server now has them: every shown row that is saved,
      // the saved version of a row whose update failed, and the rows whose
      // delete did not go through.
      const failedPatchIds = new Set(
        dirtyConfigs
          .filter((_, i) => patchResults[i]?.type !== 'success')
          .map(({ id }) => id)
      );
      const savedRows = [
        ...rows.flatMap((tw) => {
          if (tw.id === null) return [];
          if (failedPatchIds.has(tw.id)) return [baselineById.get(tw.id)!];
          return [tw];
        }),
        ...baselineTypeWellConfigs.filter(
          (tw) => tw.id && !currentIds.has(tw.id) && !deletedSet.has(tw.id)
        ),
      ];
      data?.onSaved?.(rows, savedRows);

      const failed = [
        ...(failedDelete ? [failedDelete] : []),
        ...patchResults,
        ...postResults,
      ].filter((response) => response.type !== 'success');
      if (failed.length > 0) {
        throw new Error(failed.map(typeWellErrorMessage).join('\n'));
      }
    } catch (error) {
      set(typeCurveScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Error saving type well configs',
      });
      throw error;
    }
  }
);

// --- Save validation configs ---

interface SaveValidationConfigsPayload {
  payload: {
    typeCurveScenarioId: string;
    areaConfigId: string;
    validationConfigs: ValidationConfigs;
    baselineValidationConfigs: ValidationConfigs;
  };
  onSuccess?: (updatedConfigs: ValidationConfigs) => void;
}

const buildGeneralRulePayload = (rule: GeneralValidationRule) => ({
  validationKind: rule.validationType,
  overrideVerification: rule.overrideVerification,
  thresholdValue: rule.threshold,
});

const buildStreamThresholdPayload = (threshold: StreamThreshold) => ({
  validationKind: threshold.validationType,
  stream: threshold.stream,
  overrideVerification: threshold.overrideVerification,
  thresholdValue: threshold.thresholdPercent,
  wellsToCalculate: threshold.wellSetDefinition,
});

const isGeneralRuleDirty = (
  current: GeneralValidationRule,
  baseline: GeneralValidationRule
) =>
  current.validationType !== baseline.validationType ||
  current.overrideVerification !== baseline.overrideVerification ||
  current.threshold !== baseline.threshold;

const isStreamThresholdDirty = (
  current: StreamThreshold,
  baseline: StreamThreshold
) =>
  current.validationType !== baseline.validationType ||
  current.stream !== baseline.stream ||
  current.overrideVerification !== baseline.overrideVerification ||
  current.thresholdPercent !== baseline.thresholdPercent ||
  current.wellSetDefinition !== baseline.wellSetDefinition;

const buildGeneralRuleChangedFields = (
  current: GeneralValidationRule,
  baseline: GeneralValidationRule
) => {
  const payload: Record<string, any> = {};
  if (current.validationType !== baseline.validationType)
    payload.validationKind = current.validationType;
  if (current.overrideVerification !== baseline.overrideVerification)
    payload.overrideVerification = current.overrideVerification;
  if (current.threshold !== baseline.threshold)
    payload.thresholdValue = current.threshold;
  return payload;
};

const buildStreamThresholdChangedFields = (
  current: StreamThreshold,
  baseline: StreamThreshold
) => {
  const payload: Record<string, any> = {};
  if (current.validationType !== baseline.validationType)
    payload.validationKind = current.validationType;
  if (current.stream !== baseline.stream) payload.stream = current.stream;
  if (current.overrideVerification !== baseline.overrideVerification)
    payload.overrideVerification = current.overrideVerification;
  if (current.thresholdPercent !== baseline.thresholdPercent)
    payload.thresholdValue = current.thresholdPercent;
  if (current.wellSetDefinition !== baseline.wellSetDefinition)
    payload.wellsToCalculate = current.wellSetDefinition;
  return payload;
};

export const saveValidationConfigsAtom = atom(
  (get) => get(typeCurveScenarioDataAtom),
  async (get, set, data: SaveValidationConfigsPayload) => {
    const { payload } = data;
    const {
      typeCurveScenarioId,
      areaConfigId,
      validationConfigs,
      baselineValidationConfigs,
    } = payload;

    const baseUrl = `/type_curve_scenarios/${typeCurveScenarioId}/area_configs/${areaConfigId}/validation_configs`;

    // --- General Rules ---
    const grBaselineById = new Map<string, GeneralValidationRule>();
    baselineValidationConfigs.generalRules.forEach((rule) => {
      if (rule.id) grBaselineById.set(rule.id, rule);
    });

    const newGeneralRules: GeneralValidationRule[] = [];
    const dirtyGeneralRules: { id: string; payload: Record<string, any> }[] =
      [];
    const currentGrIds = new Set<string>();

    for (const rule of validationConfigs.generalRules) {
      if (rule.id === null) {
        newGeneralRules.push(rule);
      } else {
        currentGrIds.add(rule.id);
        const baseline = grBaselineById.get(rule.id);
        if (baseline && isGeneralRuleDirty(rule, baseline)) {
          dirtyGeneralRules.push({
            id: rule.id,
            payload: buildGeneralRuleChangedFields(rule, baseline),
          });
        }
      }
    }

    const deletedGrIds = baselineValidationConfigs.generalRules
      .filter((rule) => rule.id && !currentGrIds.has(rule.id))
      .map((rule) => rule.id!);

    // --- Stream Thresholds ---
    const stBaselineById = new Map<string, StreamThreshold>();
    baselineValidationConfigs.streamThresholds.forEach((st) => {
      if (st.id) stBaselineById.set(st.id, st);
    });

    const newStreamThresholds: StreamThreshold[] = [];
    const dirtyStreamThresholds: {
      id: string;
      payload: Record<string, any>;
    }[] = [];
    const currentStIds = new Set<string>();

    for (const st of validationConfigs.streamThresholds) {
      if (st.id === null) {
        newStreamThresholds.push(st);
      } else {
        currentStIds.add(st.id);
        const baseline = stBaselineById.get(st.id);
        if (baseline && isStreamThresholdDirty(st, baseline)) {
          dirtyStreamThresholds.push({
            id: st.id,
            payload: buildStreamThresholdChangedFields(st, baseline),
          });
        }
      }
    }

    const deletedStIds = baselineValidationConfigs.streamThresholds
      .filter((st) => st.id && !currentStIds.has(st.id))
      .map((st) => st.id!);

    try {
      // PATCH dirty general rules
      const grPatchPromises = dirtyGeneralRules.map(
        ({ id, payload: updatePayload }) =>
          request.patch<ValidationConfigResponse, FailedApiResponse>(
            `${baseUrl}/${id}`,
            { validationConfig: updatePayload },
            new AbortController()
          )
      );

      // POST new general rules
      const grPostPromises = newGeneralRules.map((rule) =>
        request.post<ValidationConfigResponse, FailedApiResponse>(
          baseUrl,
          { validationConfig: buildGeneralRulePayload(rule) },
          new AbortController()
        )
      );

      // PATCH dirty stream thresholds
      const stPatchPromises = dirtyStreamThresholds.map(
        ({ id, payload: updatePayload }) =>
          request.patch<ValidationConfigResponse, FailedApiResponse>(
            `${baseUrl}/${id}`,
            { validationConfig: updatePayload },
            new AbortController()
          )
      );

      // POST new stream thresholds
      const stPostPromises = newStreamThresholds.map((st) =>
        request.post<ValidationConfigResponse, FailedApiResponse>(
          baseUrl,
          { validationConfig: buildStreamThresholdPayload(st) },
          new AbortController()
        )
      );

      // DELETE removed configs (both types share the same endpoint)
      const deletePromises = [...deletedGrIds, ...deletedStIds].map((id) =>
        request.deleteReq<null, null>(
          `${baseUrl}/${id}`,
          null,
          new AbortController()
        )
      );

      const grPatchResults = await Promise.all(grPatchPromises);
      const grPostResults = await Promise.all(grPostPromises);
      const stPatchResults = await Promise.all(stPatchPromises);
      const stPostResults = await Promise.all(stPostPromises);
      await Promise.all(deletePromises);

      for (const response of [
        ...grPatchResults,
        ...grPostResults,
        ...stPatchResults,
        ...stPostResults,
      ]) {
        if (response.type === 'error') {
          const errors = response.data?.errors;
          let msg = '';
          errors?.forEach((error: any) => {
            msg += `${error.message}\n`;
          });
          throw new Error(msg || 'Error saving validation config');
        } else if (response.type !== 'success') {
          throw new Error('Error saving validation config');
        }
      }

      // Build updated general rules with server-assigned IDs
      let grPostIndex = 0;
      const updatedGeneralRules = validationConfigs.generalRules.map((rule) => {
        if (rule.id !== null) return rule;
        const response = grPostResults[grPostIndex++];
        if (response?.type === 'success' && response.data?.id) {
          return { ...rule, id: String(response.data.id) };
        }
        return rule;
      });

      // Build updated stream thresholds with server-assigned IDs
      let stPostIndex = 0;
      const updatedStreamThresholds = validationConfigs.streamThresholds.map(
        (st) => {
          if (st.id !== null) return st;
          const response = stPostResults[stPostIndex++];
          if (response?.type === 'success' && response.data?.id) {
            return { ...st, id: String(response.data.id) };
          }
          return st;
        }
      );

      data?.onSuccess?.({
        generalRules: updatedGeneralRules,
        streamThresholds: updatedStreamThresholds,
      });
    } catch (error) {
      set(typeCurveScenarioDataAtom, {
        loading: false,
        error:
          error instanceof Error
            ? error.message
            : 'Error saving validation configs',
      });
      throw error;
    }
  }
);

// --- Validate area config ---

interface ValidateAreaConfigPayload {
  payload: {
    typeCurveScenarioId: string;
    areaGeometryId: number;
    developmentType: string;
    operatorNames?: string[];
    subAreas?: Array<{ typeCurveAreaId?: number; developmentAreaId?: number }>;
    excludeAreaConfigId?: number;
  };
  onSuccess?: (data: ValidateAreaConfigResponse) => void;
  onError?: (error: string) => void;
}

export const validateAreaConfigBaseAtom = atom<{
  loading: boolean;
  error: string | null;
}>({ loading: false, error: null });

export const validateAreaConfigAtom = atom(
  (get) => get(validateAreaConfigBaseAtom),
  async (get, set, data: ValidateAreaConfigPayload) => {
    set(validateAreaConfigBaseAtom, { loading: true, error: null });

    try {
      const { payload } = data;
      const response = await request.post<
        ValidateAreaConfigResponse,
        FailedApiResponse
      >(
        `/type_curve_scenarios/${payload.typeCurveScenarioId}/validate_area_config`,
        {
          areaGeometryId: payload.areaGeometryId,
          developmentType: payload.developmentType,
          ...(payload.operatorNames?.length
            ? { operatorNames: payload.operatorNames }
            : {}),
          ...(payload.subAreas?.length ? { subAreas: payload.subAreas } : {}),
          ...(payload.excludeAreaConfigId
            ? { excludeAreaConfigId: payload.excludeAreaConfigId }
            : {}),
        },
        new AbortController()
      );

      if (response.type !== 'success') {
        throw new Error('Validation request failed');
      }

      set(validateAreaConfigBaseAtom, { loading: false, error: null });
      data?.onSuccess?.(response.data);
    } catch (error) {
      const msg =
        error instanceof Error ? error.message : 'Error validating area config';
      set(validateAreaConfigBaseAtom, { loading: false, error: msg });
      data?.onError?.(msg);
    }
  }
);
